import styles from "./invitation.module.css";
import axios from "services/api";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import tokenService from "services/token.service";
import instance from "services/api";
import Spinner from "components/functional/spinner";
import LanguageSelector from "components/functional/language-selector";
import { ToastService, ToastComponent } from "services/toast.service";

const Invitation = () => {
  const { t } = useTranslation();
  let { invitation_id } = useParams();
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);
  const [retrieveInvitationLoading, setRetrieveInvitationLoading] =
    useState(true);
  const [informations, setInformations] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const retrieveInvitation = async () => {
    try {
      setRetrieveInvitationLoading(true);
      const response = await axios.get(`team-invitations/${invitation_id}`);
      let data = response.data;
      setData(data);
      setInformations((prev) => ({
        ...prev,
        email: data.email || "", // Ensure email is set correctly
      }));
    } catch (error) {
      console.log(error);
      navigate("/login");
    } finally {
      setRetrieveInvitationLoading(false);
    }
  };

  const registerUser = async (e) => {
    e.preventDefault();
    try {
      if (!validateForm()) return;
      setLoading(true);
      tokenService.removeToken();
      let { role, team_id, organization_id } = data;
      let { first_name, last_name, email, password } = informations;
      const response = await instance.post("users/register", {
        email,
        password,
        first_name,
        last_name,
        role,
        team_id,
        organization_id,
      });

      tokenService.setToken(response.data.token);
      navigate("/dashboard");
    } catch (error) {
      ToastService.showErrorToast(t("toast.register-duplicate-user"));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!informations.first_name) newErrors.first_name = t("errors.required");
    if (!informations.last_name) newErrors.last_name = t("errors.required");
    if (!informations.email) newErrors.email = t("errors.required");
    if (!informations.password) newErrors.password = t("errors.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    retrieveInvitation();
  }, []);

  if (retrieveInvitationLoading) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <ToastComponent />
      <div className={styles.content}>
        <section className={styles.description}>
          <div className={styles["logo-wrapper"]}>
            <img src="/logo.png" width="40" height="38" alt="" />
            <LanguageSelector />
          </div>
          <header className={styles.header}>
            <h1 className={styles.title}>
              {t("invitation.title-1", {
                name: `${data.first_name} ${data.last_name}`,
              })}
              <span className={styles.team}>{data.team_name}</span>
              <span>{t("invitation.title-2")}</span>
              <span className={styles.role}>
                {t(`invitation.card.role-${data.role}`)}
              </span>
            </h1>
            <p className="subtitle-1-variant">{t("invitation.description")}</p>
          </header>
        </section>
        <div className={styles.register}>
          <form className={styles.form}>
            <div className={styles["form-header"]}>
              <h2 className="title-2-variant">
                {t("onboarding.admin.informations-step.personal-data.title")}
              </h2>
              <p className="subtitle-1-variant">
                {t(
                  "onboarding.admin.informations-step.personal-data.description"
                )}
              </p>
            </div>
            <div className={styles["form-content"]}>
              <div className="form-group">
                <label htmlFor="firstName" className="required">
                  {t(
                    "onboarding.admin.informations-step.personal-data.first-name"
                  )}
                </label>
                <input
                  type="text"
                  id="firstName"
                  className={`grey-input ${errors.first_name && "error"}`}
                  value={informations.first_name}
                  onChange={(e) => {
                    setInformations({
                      ...informations,
                      first_name: e.target.value,
                    });
                    setErrors({ ...errors, first_name: null });
                  }}
                  placeholder={t(
                    "onboarding.admin.informations-step.personal-data.first-name-placeholder"
                  )}
                />
                {errors.first_name && (
                  <p className="error-message">{t("errors.required")}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="lastName" className="required">
                  {t(
                    "onboarding.admin.informations-step.personal-data.last-name"
                  )}
                </label>
                <input
                  type="text"
                  id="lastName"
                  className={`grey-input ${errors.last_name && "error"}`}
                  value={informations.last_name}
                  onChange={(e) => {
                    setInformations({
                      ...informations,
                      last_name: e.target.value,
                    });
                    setErrors({ ...errors, last_name: null });
                  }}
                  placeholder={t(
                    "onboarding.admin.informations-step.personal-data.last-name-placeholder"
                  )}
                />
                {errors.last_name && (
                  <p className="error-message">{t("errors.required")}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email" className="required">
                  {t("onboarding.admin.informations-step.personal-data.email")}
                </label>
                <input
                  type="email"
                  id="email"
                  className={`grey-input ${errors.email && "error"}`}
                  value={informations.email}
                  disabled={data.email}
                  onChange={(e) => {
                    setInformations({
                      ...informations,
                      email: e.target.value,
                    });
                    setErrors({ ...errors, email: null });
                  }}
                  placeholder={t(
                    "onboarding.admin.informations-step.personal-data.email-placeholder"
                  )}
                />
                {errors.email && (
                  <p className="error-message">{t("errors.required")}</p>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="password" className="required">
                  {t(
                    "onboarding.admin.informations-step.personal-data.password"
                  )}
                </label>
                <input
                  type="password"
                  id="password"
                  className={`grey-input ${errors.password && "error"}`}
                  value={informations.password}
                  onChange={(e) => {
                    setInformations({
                      ...informations,
                      password: e.target.value,
                    });
                    setErrors({ ...errors, password: null });
                  }}
                  placeholder={t(
                    "onboarding.admin.informations-step.personal-data.password-placeholder"
                  )}
                />
                {errors.password && (
                  <p className="error-message">{t("errors.required")}</p>
                )}
              </div>
            </div>
          </form>
          <div className={styles.actions}>
            <button
              className="btn btn--primary"
              type="submit"
              onClick={registerUser}
            >
              {t("onboarding.admin.informations-step.cta")}
              {loading && <Spinner className="action-loader" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invitation;
